import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import { SNOW_AUTOPOPULATION_SERVICE_ENDPOINT, SNOW_AUTOPOPULATION_SERVICE_REGION } from 'src/config';
import { getJwtToken } from 'src/authentication/cognito';
import { AutoPopulationDefinition } from '@amzn/snow-auto-population-service-typescript-client';

export type { AutoPopulationDefinition };

export interface AutoPopulationAttributeValue {
	stringValue?: string;
	numberValue?: number;
	booleanValue?: boolean;
	listValue?: string[];
}

export interface AutoPopulationMapping {
	workflowId: string;
	definition: Record<string, AutoPopulationDefinition>;
}

export const emptyAutoPopulationMapping = (): AutoPopulationMapping => ({
	workflowId: '',
	definition: {},
});

/**
 * Calls SNOWAutoPopulationService as the signed-in user. The access token is read right before
 * each call, so a page left open past the token's lifetime never sends an expired one. Resolves
 * to the raw response and throws when the call fails.
 */
export const callAutoPopulationService = async (request: {
	path: string;
	method: 'GET' | 'POST';
	body?: unknown;
}) =>
	callSNOWDataService({
		...request,
		body: request.body ?? null,
		url: SNOW_AUTOPOPULATION_SERVICE_ENDPOINT,
		region: SNOW_AUTOPOPULATION_SERVICE_REGION,
		headers: {
			'x-jwt-token': await getJwtToken(),
		},
	});

/**
 * Single entry point for GET /autoPopulation/definitions/{workflowId}.
 *
 * Resolves to an empty mapping for a workflow with no definition configured, and throws when the
 * lookup itself fails, so callers can tell those two cases apart.
 */
export const fetchAutoPopulationMapping = async (workflowId: string): Promise<AutoPopulationMapping> => {
	if (!workflowId) return emptyAutoPopulationMapping();

	const response = await callAutoPopulationService({
		path: `/autoPopulation/definitions/${workflowId}`,
		method: 'GET',
	}) as Partial<AutoPopulationMapping> | null;

	return {
		workflowId: response?.workflowId || workflowId,
		definition: response?.definition || {},
	};
};

export const hasAutoPopulationDefinitions = (mapping: AutoPopulationMapping | null | undefined): boolean =>
	Object.keys(mapping?.definition || {}).length > 0;

export interface AutoPopulationExecutionResult {
	populatedAttributes: Record<string, AutoPopulationAttributeValue>;
	/**
	 * Visibility decisions for the attributes this execution touched. Delta semantics: an absent
	 * attribute is unchanged from its previous state, so an empty map means "nothing changed".
	 */
	attributeVisibility: Record<string, boolean>;
}

/**
 * Single entry point for POST /autoPopulation/executions. Throws on failure so callers can
 * surface their own error state.
 */
export const initiateAutoPopulation = async (
	request: {
		workflowId: string;
		attributeUpdated: string;
		attributeValues: Record<string, AutoPopulationAttributeValue>;
	},
): Promise<AutoPopulationExecutionResult> => {
	const response = await callAutoPopulationService({
		path: '/autoPopulation/executions',
		method: 'POST',
		body: request,
	}) as Partial<AutoPopulationExecutionResult> | null;

	return {
		populatedAttributes: response?.populatedAttributes || {},
		attributeVisibility: response?.attributeVisibility || {},
	};
};